/* eslint-disable prettier/prettier */
import { FunctionComponent, useState } from "react";
import * as XLSX from "xlsx";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { FileUpload } from "primereact/fileupload";
import { Tag } from "primereact/tag";
import AdminServices from "services/admin-services";
import InvestorService from "services/invoice-service";
import { handlingUrlParams } from "Utils/AppUtils";
import { setToaster } from "common/toastStore";
import Loader from "common/Loader";

interface ManualDeliveryProps {}

const ManualDelivery: FunctionComponent<ManualDeliveryProps> = () => {
  const [searchValue, setSearchValue] = useState<any>(null);
  const [investorSuggestions, setInvestorSuggestions] = useState<any[]>([]);
  const [selectedInvestor, setSelectedInvestor] = useState<any>(null);
  const [packageRows, setPackageRows] = useState<any[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUploadKey, setFileUploadKey] = useState<number>(0);
  const [totalRecordCount, setTotalRecordCount] = useState<number | null>(null);
  const [uploadedRecordCount, setUploadedRecordCount] = useState<number | null>(
    null,
  );
  const [duplicateRecordCount, setDuplicateRecordCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const selectedInvestorId =
    selectedInvestor?.id ||
    selectedInvestor?.investorId ||
    searchValue?.id ||
    searchValue?.investorId ||
    0;

  const searchInvestors = async (event: any) => {
    const query = (event?.query || "").trim();

    if (query.length < 3) {
      setInvestorSuggestions([]);
      return;
    }

    try {
      const params = handlingUrlParams({
        PageNumber: 1,
        PageSize: 20,
        SearchTerm: query,
        IncludeInactive: true,
      });

      const response: any = await InvestorService.getInvestorsList(params);
      setInvestorSuggestions(response?.data || []);
    } catch (error) {
      console.warn(error);
      setInvestorSuggestions([]);
    }
  };

  const loadInvestorPackages = async (investorId: number) => {
    setLoading(true);
    try {
      const response: any = await AdminServices.getInvestorPackagesNew(
        handlingUrlParams({ investorId }),
      );
      setPackageRows(response?.data || []);
    } catch (error) {
      console.warn(error);
      setPackageRows([]);
      setToaster({
        severity: "error",
        summary: "Error",
        detail: "Failed to load investor packages.",
      });
    } finally {
      setLoading(false);
    }
  };

  const onInvestorChange = (e: any) => {
    setSearchValue(e.value);

    if (typeof e.value === "string") {
      setSelectedInvestor(null);
      setPackageRows([]);
    }
  };

  const onInvestorSelect = async (e: any) => {
    const investor = e?.value;
    setSelectedInvestor(investor || null);
    setSearchValue(investor || null);

    if (investor?.id) {
      await loadInvestorPackages(investor.id);
    } else {
      setPackageRows([]);
    }
  };

  const parseLoanNumbersFromWorksheet = (rows: any[][]) => {
    const normalizedRows = rows
      .map((row) => (Array.isArray(row) ? row : [row]))
      .map((row) => row.map((cell) => String(cell ?? "").trim()))
      .filter((row) => row.some(Boolean));

    if (!normalizedRows.length) {
      return {
        loanNumbers: [] as string[],
        totalRecordCount: 0,
        duplicateCount: 0,
        error: "Uploaded file is empty.",
      };
    }

    const header = normalizedRows[0]?.[0]?.trim().toLowerCase();
    if (header !== "loannumber") {
      return {
        loanNumbers: [] as string[],
        totalRecordCount: 0,
        duplicateCount: 0,
        error: "XLSX file must contain header LoanNumber.",
      };
    }

    const rawLoanNumbers = normalizedRows
      .slice(1)
      .map((row) => row[0]?.trim())
      .filter(Boolean);

    const uniqueMap = new Map<string, string>();
    rawLoanNumbers.forEach((loanNumber) => {
      const key = loanNumber.toLowerCase();
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, loanNumber);
      }
    });

    const loanNumbers = Array.from(uniqueMap.values());
    const totalRecordCount = rawLoanNumbers.length;
    const duplicateCount = rawLoanNumbers.length - loanNumbers.length;

    return {
      loanNumbers,
      totalRecordCount,
      duplicateCount,
      error: "",
    };
  };

  const parseLoanNumbersFromXlsx = async (file: File) => {
    const fileBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(fileBuffer, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];

    if (!firstSheetName) {
      return {
        loanNumbers: [] as string[],
        totalRecordCount: 0,
        duplicateCount: 0,
        error: "Uploaded file is empty.",
      };
    }

    const worksheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      raw: false,
      defval: "",
    }) as any[][];

    return parseLoanNumbersFromWorksheet(rows);
  };

  const processSelectedFile = async (file: File | null) => {
    setSelectedFile(file);

    if (!file) {
      setTotalRecordCount(null);
      setUploadedRecordCount(null);
      setDuplicateRecordCount(0);
      return;
    }

    try {
      const { loanNumbers, error, duplicateCount, totalRecordCount } =
        await parseLoanNumbersFromXlsx(file);

      if (error) {
        setTotalRecordCount(null);
        setUploadedRecordCount(null);
        setDuplicateRecordCount(0);
        setToaster({
          severity: "warn",
          summary: "Validation",
          detail: error,
        });
        return;
      }

      setTotalRecordCount(totalRecordCount);
      setUploadedRecordCount(loanNumbers.length);
      setDuplicateRecordCount(duplicateCount);
    } catch (error) {
      console.error(error);
      setTotalRecordCount(null);
      setUploadedRecordCount(null);
      setDuplicateRecordCount(0);
      setToaster({
        severity: "error",
        summary: "Error",
        detail: "Unable to read uploaded file.",
      });
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setTotalRecordCount(null);
    setUploadedRecordCount(null);
    setDuplicateRecordCount(0);
    setFileUploadKey((prev) => prev + 1);
  };

  const onSubmitManualDelivery = async () => {
    if (!selectedFile) return;

    if (!selectedInvestorId) {
      setToaster({
        severity: "warn",
        summary: "Validation",
        detail: "Please select an investor before submitting.",
      });
      return;
    }

    setSubmitting(true);
    try {
      const { loanNumbers, error, duplicateCount } =
        await parseLoanNumbersFromXlsx(selectedFile);

      if (error) {
        setToaster({
          severity: "warn",
          summary: "Validation",
          detail: error,
        });
        return;
      }

      if (!loanNumbers.length) {
        setToaster({
          severity: "warn",
          summary: "Validation",
          detail: "No loan numbers found in uploaded file.",
        });
        return;
      }

      await AdminServices.createManualDelivery({
        investorId: selectedInvestorId,
        loanNumbers,
      });

      setToaster({
        severity: "success",
        summary: "Success",
        detail:
          duplicateCount > 0
            ? `Manual delivery submitted successfully. ${duplicateCount} duplicate entr${duplicateCount === 1 ? "y" : "ies"} were ignored.`
            : "Manual delivery submitted successfully.",
      });

      clearSelectedFile();
    } catch (e: any) {
      console.error(e);
      setToaster({
        severity: "error",
        summary: "Error",
        detail: e?.response?.data || "Failed to submit manual delivery.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const downloadLoanNumberTemplate = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([["LoanNumber"]]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    const workbookBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([workbookBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "manual_delivery_template.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const packageNameTemplate = (rowData: any) => {
    return rowData?.package?.name || rowData?.name || "N/A";
  };

  const packageDescriptionTemplate = (rowData: any) => {
    return rowData?.package?.description || rowData?.description || "";
  };

  const packageActiveTemplate = (rowData: any) => {
    const isActive = !!(rowData?.package?.isActive ?? rowData?.isActive);
    return (
      <Tag
        value={isActive ? "Yes" : "No"}
        severity={isActive ? "success" : "danger"}
      />
    );
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Manual Delivery</h2>
        </div>

        <div className="rounded-md border border-gray-200 bg-white p-4">
          <label className="block text-sm font-semibold mb-2">Investor</label>
          <AutoComplete
            value={searchValue}
            suggestions={investorSuggestions}
            completeMethod={searchInvestors}
            field="name"
            onChange={onInvestorChange}
            onSelect={onInvestorSelect}
            placeholder="Search investor by name"
            className="w-full"
            inputClassName="w-full"
            forceSelection
          />
          <small className="text-xs text-amber-700 font-semibold mt-1 block">
            Enter at least 3 characters to search for investor name.
          </small>
        </div>

        {selectedInvestor && (
          <div className="rounded-md border border-gray-200 bg-white p-4">
            <div className="text-sm font-semibold mb-2">Investor Packages</div>

            <DataTable
              value={packageRows}
              className="p-datatable-sm"
              stripedRows
              responsiveLayout="scroll"
              emptyMessage="No packages found for selected investor."
            >
              <Column field="id" header="Id" style={{ minWidth: "90px" }} />
              <Column
                header="Name"
                body={packageNameTemplate}
                style={{ minWidth: "240px" }}
              />
              <Column
                header="Description"
                body={packageDescriptionTemplate}
                style={{ minWidth: "260px" }}
              />
              <Column
                header="Active"
                body={packageActiveTemplate}
                style={{ minWidth: "120px" }}
              />
            </DataTable>
          </div>
        )}

        <div className="rounded-md border border-gray-200 bg-white p-4">
          <label className="block text-sm font-semibold mb-2">Upload File</label>
          <div className="mb-3">
            <Button
              type="button"
              icon="pi pi-download"
              label="Download XLSX Template"
              className="p-button-sm p-button-outlined p-button-secondary"
              onClick={downloadLoanNumberTemplate}
            />
            <small className="text-xs text-gray-600 mt-1 block">
              Template contains one required column header: LoanNumber.
            </small>
          </div>
          <FileUpload
            key={fileUploadKey}
            mode="basic"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            chooseLabel={selectedFile ? "Change File" : "Choose File"}
            multiple={false}
            onSelect={async (e) => {
              const file = e?.files?.[0] || null;
              await processSelectedFile(file);
            }}
          />
          {selectedFile && (
            <div className="mt-2">
              <Button
                type="button"
                label="Clear Selected File"
                icon="pi pi-times"
                className="p-button-sm p-button-text p-button-secondary"
                onClick={clearSelectedFile}
              />
            </div>
          )}
          {selectedFile && (
            <small className="text-xs text-gray-700 mt-2 block">
              Selected file: {selectedFile.name}
            </small>
          )}
          {totalRecordCount !== null && (
            <small className="text-xs text-gray-700 mt-2 block">
              Total Records Found: {totalRecordCount}
            </small>
          )}
          {uploadedRecordCount !== null && (
            <small className="text-xs text-gray-700 mt-2 block">
              Valid Records found: {uploadedRecordCount}
            </small>
          )}
          {duplicateRecordCount > 0 && (
            <small className="text-xs text-amber-700 font-semibold mt-1 block">
              Duplicates found: {duplicateRecordCount}. These duplicate entries
              will be ignored.
            </small>
          )}

          <div className="mt-4">
            <Button
              label={submitting ? "Submitting..." : "Submit"}
              icon="pi pi-check"
              className="p-button-sm p-button-success p-button-outlined"
              disabled={!selectedInvestorId || !selectedFile || submitting}
              onClick={onSubmitManualDelivery}
            />
          </div>
        </div>
      </div>

      {(loading || submitting) && <Loader />}
    </>
  );
};

export default ManualDelivery;